/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Button } from '@ziynx/components/buttons';
import { Heading } from '@ziynx/components/typography';
import { Constants } from '@ziynx/components/layout';
import { eventNames } from '@ziynx/analytics';
import ActionTypes from '@ziynx/action-types';
import Colors from '@ziynx/theme/colors';
import Link from '@ziynx/components/links';
import Input from '@ziynx/components/inputs';
import Image from '@ziynx/components/images';
import Modal from '@ziynx/components/modals';
import MediaCropper from '@ziynx/components/croppers';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import {
  withAppManager,
  getCroppedMedia,
  LoadingSpinner,
  getMeasures,
  transformCropDimensions,
  getPatchCropDimensions,
} from '@ziynx/components/utilities';
import type {
  MediaUploadReference, CroppedObject, CroppedArea,
} from '@ziynx/flow-types/media';
import type { ImageUploadConfig } from '@ziynx/flow-types/companyProfile';
import ModalConstants from './Constants';

type Props = $ReadOnly<{|
  /**
   * Boolean to open modal
   */
  open?: boolean,
  /**
   * Upload config object
   */
  imageUploadConfig?: ImageUploadConfig,
  /**
   * Custom function to close modal
   */
  onClose: () => void,

  getMediaId?: (any) => any,
|}>;

const MODAL_PADDING = {
  mobileSmall: `${ModalConstants?.modalPadding}px 16px`,
  mobile: `${ModalConstants?.modalPadding}px 16px`,
  tablet: `${ModalConstants?.modalPadding}px 16px`,
  desktopSmall: `${ModalConstants?.modalPadding}px 24px`,
  default: `${ModalConstants?.modalPadding}px 48px`,
};

// For fallback, full browser support
const VIDEO_WRAPPER_MEASURES = (viewport) => {
  switch (viewport) {
  case 'mobileSmall':
    return '144px';
  case 'mobile':
    return '196px';
  case 'tablet':
    return '197px';
  case 'desktopSmall':
    return '360px';
  default:
    return '508px';
  }
};

const BUTTON_WRAPPER_WIDTH = {
  mobileSmall: '256px',
  mobile: '349px',
  tablet: '351px',
  default: '392px',
};

const BUTTON_CONTROL_WIDTH = {
  mobileSmall: '100%',
  mobile: '100%',
  tablet: '168px',
  default: '188px',
};

const StyledModal = styled(Modal)`
  padding: ${({ viewport }) => getMeasures(MODAL_PADDING, viewport)};
  overflow: hidden;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
`;

const StyledButton = styled(Button)`
  ${({ isMobile, lastChild }) => isMobile && !lastChild && 'margin-bottom: 16px;'};
`;

const StyledHeadingWrapper = styled.div`
  padding-bottom: ${ModalConstants?.titlePaddingBottom}px;
  text-align: center;
`;

const ImageSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  height: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  @supports not (aspect-ratio: 1) {
    width: ${({ width }) => width};
    height: auto;
    ${({ empty, width }) => empty && `height: ${width};`}
  };
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  height: 100%;
  aspect-ratio: 1;
  ${({ empty }) => empty
    && `border-radius: ${Constants.borderRadiusLarge}px;
    border: 2px dashed ${Colors.GrayVeryLight};`}
  ${({ border }) => border
    && `border-radius: ${Constants.borderRadiusLarge}px;
    border: 1px solid ${Colors.GrayVeryLight};`}
    @supports not (aspect-ratio: 1) {
    width: ${({ width }) => width};
    height: auto;
  };
`;

const UploadButtonsWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  justify-content: space-between;
  width: ${({ viewport }) => getMeasures(BUTTON_WRAPPER_WIDTH, viewport)};
  margin-top: ${({ largeMargin }) => (largeMargin ? '54px' : '0px')};
`;

const CropperWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  position: relative;
`;

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${({ viewport }) => getMeasures(BUTTON_WRAPPER_WIDTH, viewport)};
  margin-top: ${ModalConstants?.cropperButtonGroupMargin}px;
`;

const CropperButtonsWrapper = styled(UploadButtonsWrapper)`
  margin-top: 40px;
`;

/**
 * Photo or media uploader screen used to upload/edit profile pictures or videos
 */
const UploadPhotoModal = (props: ManagedProps<Props>): React$Node => {
  const {
    open, onClose, viewport, imageUploadConfig, getMediaId,
  } = props;

  const { destination, existingPhoto = {}, mediaUploadReferenceId } = imageUploadConfig || {};

  const [previewLoading, setLoading] = useState(false);
  // Keeps track of image being uploaded
  const [uploadedImage, setUploadedImage] = useState(undefined);
  (uploadedImage: File | typeof undefined);
  // Image File from inputFile
  const [rawImage, setRawImage] = useState('');
  (rawImage: string | null | ArrayBuffer);
  // Cropped image
  const [croppedImage, setCroppedImage] = useState('');
  (croppedImage: string);
  // State to render the image/video cropper
  const [shouldRenderCropper, setRenderCropper] = useState(false);
  (shouldRenderCropper: boolean);
  // Keeps track of zoom level
  const [zoom, setZoom] = useState(1);
  (zoom: number);
  // Keeps track of croppedAreaPixels
  const [croppedAreaPixels, setCroppedAreaPixels] = useState({});
  (croppedAreaPixels: CroppedArea);

  const dispatch = useDispatch();

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  const _openCropper = (): void => {
    setRenderCropper(true);
  };

  const _closeCropper = (): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.canceled_crop,
    });
    setRenderCropper(false);
  };

  /**
   * Function to grab the file from machine and
   * convert to Data URL FOR NOW.
   */
  const _getRawFile = (file: File): void => {
    setUploadedImage(file);
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result);
      _openCropper();
    };

    reader.readAsDataURL(file);
  };

  /**
   * Render image section
   */
  const _renderImageSection = (): React$Node => {
    if (croppedImage || existingPhoto?.uri) {
      let imageAlt = 'company profile';
      if (destination === 'originalImage') imageAlt = 'original';
      if (destination === 'nilImage') imageAlt = 'NIL post';
      return (
        <ImageSectionWrapper width={VIDEO_WRAPPER_MEASURES(viewport)}>
          <ImageWrapper withEdit viewport={viewport}>
            <Image
              isBackground
              src={croppedImage || existingPhoto?.uri}
              alt={`Cropped ${imageAlt} image`}
              layoutConfig={{
                border: `1px solid ${Colors.GrayLight}`,
                borderRadius: `${Constants.borderRadiusMedium}px`,
              }}
            />
          </ImageWrapper>
        </ImageSectionWrapper>

      );
    }
    return (
      <ImageWrapper
        width={VIDEO_WRAPPER_MEASURES(viewport)}
        empty
      >
        {!previewLoading && !existingPhoto?.uri && (
          <Heading medium color={Colors.Gray}>
            No photo
          </Heading>
        )}
        {previewLoading && (
          <LoadingSpinner />
        )}
      </ImageWrapper>
    );
  };

  const _getCroppedImage = useCallback(async (): Promise<void> => {
    setLoading(true);

    // send raw image from fresh upload or original passed in to get cropped preview
    const imageSource = rawImage || existingPhoto?.original?.uri;

    try {
      const croppedImageData = await getCroppedMedia({
        imageSource,
        croppedAreaPixels,
        type: 'image',
      });
      setCroppedImage(croppedImageData);
    } catch (e) {
      // TODO: handle error via banners here
      setCroppedImage('');
    }

    setLoading(false);
  }, [croppedAreaPixels]);

  const _finishCrop = (): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.cropped_photo,
    });
    existingPhoto.uri = '';
    _closeCropper();
    _getCroppedImage();
  };
  const _removeImage = (): void => {
    setRawImage('');
  };

  const _reset = (): void => {
    setCroppedImage('');
    setZoom(1);
    setCroppedAreaPixels({});
  };

  const _onCancel = (): void => {
    _removeImage();
    _reset();
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.canceled_edit_photo,
    });
    if (onClose) onClose();
  };

  const _saveImage = (): void => {
    if (getMediaId) {
      const imageRef = {
        ...uploadedImage,
        uploadReferenceId: mediaUploadReferenceId,
      };
      getMediaId(imageRef);
    }
    // If no fresh uploaded image, patch crop dimensions of current photo
    if (!rawImage) {
      const mediaUploadReference: MediaUploadReference = {
        uploadReferenceId: mediaUploadReferenceId,
      };
      dispatch({
        type: ActionTypes.media.editMediaAsset.START,
        body: getPatchCropDimensions(croppedAreaPixels),
        assetId: existingPhoto?._id,
        mediaUploadReference,
      });
    } else {
      const mediaUploadReference: MediaUploadReference = {
        uploadReferenceId: mediaUploadReferenceId,
        mediaType: 'image',
        destination: destination || 'companyProfileImage',
        imageFile: uploadedImage,
      };
      dispatch({
        type: ActionTypes.media.getPresignedUrl.START,
        body: {
          type: uploadedImage?.type || 'image/jpeg',
          cropDimensions: transformCropDimensions(croppedAreaPixels),
        },
        mediaUploadReference,
      });
    }
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.saved_photo,
    });
    _reset();
    if (onClose) onClose();
  };

  const _handleCropComplete = (result: CroppedObject): void => {
    setCroppedAreaPixels(result?.croppedAreaPixels);
  };

  const _handleOnZoomChange = (
    event: SyntheticInputEvent<HTMLInputElement>,
  ): void => {
    setZoom(event?.target?.valueAsNumber);
  };

  /** Used for InputFile. onClick already handled in Krispy */
  const _renderInputFileButton = (): React$Node => (
    <StyledButton
      isMobile={isMobile}
      // onClick={() => {
      //   dispatch({
      //     type: ActionTypes.analytics.logEvent.START,
      //     name: eventNames.changed_photo,
      //   });
      // }}
      variant="primary"
      width={getMeasures(BUTTON_CONTROL_WIDTH, viewport)}
    />
  );

  return (
    <>
      <StyledModal open={open} viewport={viewport}>
        <ModalContent>
          <StyledHeadingWrapper>
            <Heading medium color={Colors.GrayVeryDark}>
              Edit
              {' '}
              {(destination === 'originalImage' || destination === 'nilImage') ? 'thumbnail image' : 'profile photo'}
            </Heading>
          </StyledHeadingWrapper>
          {!shouldRenderCropper && (
            <>
              {_renderImageSection()}
              <ControlsWrapper>
                {(croppedImage || existingPhoto?.uri)
              && <Link variant="text" large onClick={_openCropper}>Edit crop</Link>}
                <UploadButtonsWrapper
                  isMobile={isMobile}
                  viewport={viewport}
                  empty={!croppedImage && existingPhoto?.uri}
                  largeMargin={!!croppedImage || existingPhoto?.uri}
                >
                  {!croppedImage && (
                    <Input
                      width={getMeasures(BUTTON_CONTROL_WIDTH, viewport)}
                      variant="file"
                      inputName="photo-upload"
                      onChange={_getRawFile}
                      renderButtonPrimary={_renderInputFileButton}
                    >
                      {existingPhoto?.uri ? 'Change' : 'Choose a photo'}
                    </Input>
                  )}
                  {croppedImage && (
                    <StyledButton
                      isMobile={isMobile}
                      variant="primary"
                      width={getMeasures(BUTTON_CONTROL_WIDTH, viewport)}
                      onClick={_saveImage}
                    >
                      Save
                    </StyledButton>
                  )}
                  <StyledButton
                    variant="primary"
                    lastChild
                    inverted
                    width={getMeasures(BUTTON_CONTROL_WIDTH, viewport)}
                    onClick={_onCancel}
                  >
                    Cancel
                  </StyledButton>
                </UploadButtonsWrapper>
              </ControlsWrapper>
            </>
          )}
          {shouldRenderCropper && (
            <>
              <CropperWrapper>
                <MediaCropper
                  variant="image"
                  objectFit="contain"
                  zoomWithScroll={false}
                  source={rawImage || existingPhoto?.uri}
                  aspectRatio={1}
                  zoom={zoom}
                  onCropComplete={_handleCropComplete}
                  style={{
                    containerStyle: {
                      height: '100%',
                      width: '100%',
                      // maxHeight: `calc(90vh - ${maxModalHeightOffset}px)`,
                    },
                  }}
                />
              </CropperWrapper>
              <ControlsWrapper viewport={viewport}>
                <Input
                  variant="slider"
                  label="Zoom"
                  inputName="photo-zoom-slider"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  width="100%"
                  onChange={_handleOnZoomChange}
                />
                <CropperButtonsWrapper isMobile={isMobile} viewport={viewport}>
                  <StyledButton
                    variant="primary"
                    isMobile={isMobile}
                    width={getMeasures(BUTTON_CONTROL_WIDTH, viewport)}
                    onClick={_finishCrop}
                  >
                    Crop
                  </StyledButton>
                  <StyledButton
                    variant="primary"
                    lastChild
                    inverted
                    width={getMeasures(BUTTON_CONTROL_WIDTH, viewport)}
                    onClick={_closeCropper}
                  >
                    Cancel
                  </StyledButton>
                </CropperButtonsWrapper>
              </ControlsWrapper>
            </>
          )}
        </ModalContent>
      </StyledModal>
    </>
  );
};

export default (withAppManager(UploadPhotoModal): React$ComponentType<ManagedProps<Props>>);
